import FilterPanel from '@components/FilterPanel/FilterPanel';
import React, { FunctionComponent, useEffect } from 'react';
import { Button, Checkbox, DatePicker, Form, InputNumber, Select } from 'antd';
import moment from 'moment';
import { useStore } from 'effector-react';

import registrationIps, { getList } from '@stores/registrationIps';
import useTranslate from '@hooks/useTranslate';
import componentContent from './RegistrationIpsFilters.content.json';

import css from './RegistrationIpsFilters.module.less';

const { Option } = Select;

interface RegistrationIpsFiltersProps {}

const RegistrationIpsFilters: FunctionComponent<RegistrationIpsFiltersProps> = () => {
  const { filters, preloaders } = useStore(registrationIps);
  const content = useTranslate(componentContent);
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      statuses:
        filters.statuses?.length === 2 &&
        filters.statuses.includes('UNBAN_MANUAL') &&
        filters.statuses.includes('UNBAN_AUTO')
          ? 'UNBAN'
          : filters.statuses?.[0],
      onlyWithBannedAccounts: filters.onlyWithBannedAccounts,
      minBannedAccountsRatio: filters.minBannedAccountsRatio,
      minTotalAccountsCount: filters.minTotalAccountsCount,
      maxTotalAccountsCount: filters.maxTotalAccountsCount,
      lastRegistrationDate: [
        filters.lastRegistrationDateFrom ? moment(filters.lastRegistrationDateFrom) : null,
        filters.lastRegistrationDateTo ? moment(filters.lastRegistrationDateTo) : null,
      ],
    });
  }, [filters, form]);

  const mapStatusValueToStatuses = (value?: string) => {
    if (!value) return undefined;

    if (value === 'UNBAN') {
      return ['UNBAN_MANUAL', 'UNBAN_AUTO'];
    }

    return [value];
  };

  const onFinish = (values: any) => {
    const nextFilters = {
      sort: filters.sort,
      statuses: mapStatusValueToStatuses(values.statuses),
      onlyWithBannedAccounts: !!values.onlyWithBannedAccounts,
      minBannedAccountsRatio:
        values.minBannedAccountsRatio === 0 || values.minBannedAccountsRatio
          ? values.minBannedAccountsRatio
          : undefined,
      minTotalAccountsCount: values.minTotalAccountsCount ?? undefined,
      maxTotalAccountsCount:
        values.maxTotalAccountsCount === 0 || values.maxTotalAccountsCount
          ? values.maxTotalAccountsCount
          : undefined,
      lastRegistrationDateFrom: values.lastRegistrationDate?.[0]
        ?.clone()
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss'),
      lastRegistrationDateTo: values.lastRegistrationDate?.[1]
        ?.clone()
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS'),
    };

    getList({
      filters: nextFilters,
    });
  };

  const onClear = () => {
    const resetFilters = {
      sort: 'lastRegistrationDate,desc',
      statuses: undefined,
      onlyWithBannedAccounts: true,
      minBannedAccountsRatio: undefined,
      lastRegistrationDateFrom: moment()
        .subtract(14, 'days')
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss'),
      lastRegistrationDateTo: undefined,
      minTotalAccountsCount: 1,
      maxTotalAccountsCount: 100,
    };

    form.setFieldsValue({
      statuses: undefined,
      onlyWithBannedAccounts: resetFilters.onlyWithBannedAccounts,
      minBannedAccountsRatio: resetFilters.minBannedAccountsRatio,
      minTotalAccountsCount: resetFilters.minTotalAccountsCount,
      maxTotalAccountsCount: resetFilters.maxTotalAccountsCount,
      lastRegistrationDate: [moment(resetFilters.lastRegistrationDateFrom), null],
    });

    getList({
      filters: resetFilters,
    });
  };

  return (
    <Form
      form={form}
      layout='vertical'
      className={css.root}
      onFinish={onFinish}
    >
      <FilterPanel
        actions={
          <div className={css.actions}>
            <Button
              htmlType='submit'
              type='primary'
              loading={!!preloaders.getList}
            >
              {content.search}
            </Button>

            <Button
              htmlType='button'
              onClick={onClear}
              loading={!!preloaders.getList}
            >
              {content.clear}
            </Button>
          </div>
        }
      >
        <div className={css.grid}>
          <Form.Item
            name='onlyWithBannedAccounts'
            valuePropName='checked'
            className={css.checkboxItem}
          >
            <Checkbox>{content.onlyWithBannedAccounts}</Checkbox>
          </Form.Item>

          <Form.Item
            label={content.bannedAccountsRatio}
            name='minBannedAccountsRatio'
          >
            <InputNumber
              min={0}
              max={1}
              step={0.01}
              placeholder={content.from}
              className={css.fullWidth}
            />
          </Form.Item>

          <Form.Item
            label={content.lastRegistrationDate}
            name='lastRegistrationDateFrom'
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={content.fromDate}
            />
          </Form.Item>

          <Form.Item
            label={content.totalAccountsCount}
            name='maxTotalAccountsCount'
          >
            <InputNumber
              min={0}
              placeholder={content.to}
              className={css.fullWidth}
            />
          </Form.Item>

          <Form.Item
            label={content.status}
            name='statuses'
          >
            <Select allowClear>
              <Option value='BAN'>{content.status_BAN}</Option>
              <Option value='UNBAN'>{content.status_UNBAN}</Option>
              <Option value='FREE'>{content.status_FREE}</Option>
            </Select>
          </Form.Item>
        </div>
      </FilterPanel>
    </Form>
  );
};

export default RegistrationIpsFilters;
